// import type { FilterSortArgsNoRuntime } from "./filter/sort";
import type {
  CondLeftArgsNoRuntime,
  CondRightArgsNoRuntime,
} from "./strategy-conditionals";
import type {
  FilterComparisonOperators,
  FilterFunctionArgsNoRuntime,
  SelectFunctionArgsNoRuntime,
  SortFunctionArgsNoRuntime,
  WildcardFilterOptions,
} from "./strategy-filters";
// strategy-types.ts
import type { WeightType } from "./strategy-weights";

type Nullable<T> = {
  [P in keyof T]: T[P] | null;
};

// Block Types
export type BlockType =
  | { blockType: "topLevel"; data: TopLevelStrategyBlock }
  | { blockType: "group"; data: GroupStrategyBlock }
  | { blockType: "asset"; data: AssetStrategyBlock }
  | { blockType: "conditional"; data: ConditionalStrategyBlock }
  | { blockType: "filter"; data: FilterStrategyBlock }
  | { blockType: "weight"; data: WeightAllocationGroupBlock }
  | { blockType: "placeholder"; data: PlaceholderStrategyBlock }
  | { blockType: "action-button"; data: ActionButtonStrategyBlock }
  | { blockType: "dynamic"; data: DynamicStrategyBlock }
  | { blockType: "remote"; data: RemoteStrategyBlock };

export type BlockTypeEvaluated =
  | { blockType: "topLevel"; data: TopLevelStrategyBlock }
  | { blockType: "group"; data: GroupStrategyBlock }
  | { blockType: "asset"; data: AssetStrategyBlock }
  | { blockType: "conditional"; data: ConditionalStrategyBlock }
  | { blockType: "filter"; data: FilterStrategyBlock }
  | { blockType: "weight"; data: WeightAllocationGroupBlock }
  | { blockType: "placeholder"; data: PlaceholderStrategyBlock }
  | { blockType: "action-button"; data: ActionButtonStrategyBlock }
  | { blockType: "dynamic"; data: DynamicStrategyBlockEvaluated }
  | { blockType: "remote"; data: RemoteStrategyBlockEvaluated };

export type BlockTypeType = BlockType["blockType"];
export type BlockTypeTypeEvaluated = BlockTypeEvaluated["blockType"];

export const AVAILABLE_BLOCK_TYPES: BlockType["blockType"][] = [
  "topLevel",
  "group",
  "asset",
  "conditional",
  "filter",
  "weight",
  "placeholder",
  "action-button",
  "dynamic",
  "remote",
];

// export const AVAILABLE_BLOCK_TYPES_EVALUATED: BlockTypeEvaluated["blockType"][] = [
//   "topLevel",
//   "group",
//   "asset",
//   "conditional",
//   "filter",
//   "weight",
//   "placeholder",
//   "action-button",
//   "dynamic",
//   "remote",
// ];

export enum BlockTypeEnum {
  TopLevel = "topLevel",
  Group = "group",
  Asset = "asset",
  Conditional = "conditional",
  Filter = "filter",
  Weight = "weight",
  Placeholder = "placeholder",
  ActionButton = "action-button",
  Dynamic = "dynamic",
  Remote = "remote",
}

export type BlockTypeEvaluatedEnum = BlockTypeEvaluated["blockType"];

// Type Aliases
// for reference: 'greater' means left value is [greater] than right value
export type ConditionalComparison =
  | "greater"
  | "less"
  | "greaterOrEqual"
  | "lessOrEqual"
  | "equal"
  | null;

// Re-export types and typeguards from @repo/types to avoid duplication
import type { Decimal } from "@repo/decimal";
import type {
  AssetIdFormat,
  AssetPairIdFormat,
  Caip19Id,
  EthOrSolanaAssetIdFormat,
  UnsetAssetIdFormat,
} from "@repo/types";
export type {
  AssetIdFormat,
  EthOrSolanaAssetIdFormat,
  UnsetAssetIdFormat,
  Caip19Id,
  AssetPairIdFormat,
};
export { isAssetIdFormat, isAssetIdsFormat } from "@repo/types";

// Block Rules
export const DefaultBlockRules: { [key in BlockType["blockType"]]: BlockRule } =
  {
    topLevel: {
      requiresParentNodeType: null,
      requiresChildNodeType: "weight",
      acceptedChildrenNodeTypes: ["weight"],
      canAddChildren: true,
      disableDelete: true,
    },
    group: {
      requiresParentNodeType: null,
      requiresChildNodeType: "weight",
      acceptedChildrenNodeTypes: ["weight"],
      canAddChildren: true,
      disableDelete: false,
    },
    asset: {
      requiresParentNodeType: null,
      requiresChildNodeType: null,
      acceptedChildrenNodeTypes: [],
      canAddChildren: false,
      disableDelete: false,
    },
    remote: {
      requiresParentNodeType: "weight", // right?
      requiresChildNodeType: null,
      acceptedChildrenNodeTypes: [],
      canAddChildren: false,
      disableDelete: false,
    },
    conditional: {
      requiresParentNodeType: "weight",
      requiresChildNodeType: null,
      acceptedChildrenNodeTypes: [
        "weight",
        "placeholder",
        "asset",
        "filter",
        "conditional", // HACK(johnrjj) - Technically conditionals as children of conditionals are valid, they just need to be wrapped in a weight block.
        // Eventually we should break out 'show in UI' vs 'validate in strategy' (eg we should show conditional as an available child and automatically wrap it in a weight block)
        // So that the 'show in ui' has 'conditiona' as an option, and the 'validate in strategy' has 'weight' as an option and not 'conditional' as an option.
        // Since this is a special case, we'll just hardcode it for now.
      ],
      canAddChildren: false,
      disableDelete: false,
    },
    filter: {
      requiresParentNodeType: "weight",
      requiresChildNodeType: null,
      acceptedChildrenNodeTypes: ["asset", "placeholder"],
      canAddChildren: true,
      disableDelete: false,
    },
    dynamic: {
      requiresParentNodeType: "weight",
      requiresChildNodeType: null,
      acceptedChildrenNodeTypes: [],
      canAddChildren: false,
      disableDelete: false,
    },
    weight: {
      requiresParentNodeType: null,
      requiresChildNodeType: null,
      acceptedChildrenNodeTypes: [
        "weight",
        "placeholder",
        "asset",
        "filter",
        "conditional",
        "dynamic",
        "remote",
      ],
      canAddChildren: true,
      disableDelete: false,
    },
    placeholder: {
      requiresParentNodeType: null,
      requiresChildNodeType: null,
      acceptedChildrenNodeTypes: [],
      canAddChildren: false,
      disableDelete: false,
    },
    "action-button": {
      requiresParentNodeType: null,
      requiresChildNodeType: null,
      acceptedChildrenNodeTypes: [],
      canAddChildren: false,
      disableDelete: false,
    },
  };

export interface BlockRule {
  requiresParentNodeType: BlockType["blockType"] | null;
  requiresChildNodeType: BlockType["blockType"] | null;
  acceptedChildrenNodeTypes: BlockType["blockType"][];
  canAddChildren: boolean;
  disableDelete: boolean;
}

// Block State
export interface BaseStrategyBlock {
  blockIdForInstance: string;
  children?: AcceptedStrategyBlocks | AcceptedStrategyBlocks[];
  parentNodeId: string;
}

export interface PlaceholderStrategyBlock extends BaseStrategyBlock {
  blockType: "placeholder";
}

export interface TopLevelStrategyBlock extends BaseStrategyBlock {
  blockType: "topLevel";
  name: string;
  children: WeightAllocationGroupBlock;
}

export interface GroupStrategyBlock extends BaseStrategyBlock {
  blockType: "group";
  name: string;
  children: WeightAllocationGroupBlock;
}

export interface TokenAssetStrategyBlock extends BaseStrategyBlock {
  blockType: "asset";
  // This is always the asset/ underlying token (e.g. ETH, USDC, DAI, etc.)
  assetId: EthOrSolanaAssetIdFormat | UnsetAssetIdFormat;
  // This is the (optional) defi token (e.g. cDAI, aDAI, etc.)
  // ie the asset above[underlying] deposited into a defi protocol.
  // for now we only support compound and aave and supplying only.
  defiToken?: {
    assetId: EthOrSolanaAssetIdFormat | UnsetAssetIdFormat;
    underlyingAssetId: EthOrSolanaAssetIdFormat | UnsetAssetIdFormat; // same as assetId in original asset block. just to make it explicit/keep data together in this object
    type: "defi";
    category: "lending";
  };
  /**
   * Explicit yield allocation targets.
   * Allows specifying multiple yield sources with precise percentage allocations.
   *
   * @example
   * ```typescript
   * yieldTargets: [
   *   { yieldSourceId: "aave:v3:ausdc:8453", percent: 50 },
   *   { yieldSourceId: "compound:v3:cusdc:8453", percent: 30 }
   * ]
   * ```
   */
  yieldTargets?: YieldTarget[];
}

/**
 * Durable canonical-asset vocabulary introduced for producer-scoped strategy
 * documents. Issuance selection is deliberately absent: a canonical strategy
 * is resolved to concrete token assets only at execution compile time.
 */
export interface CanonicalAssetStrategyBlock extends BaseStrategyBlock {
  blockType: "asset";
  assetCanonicalId: string;
  assetId?: never;
  defiToken?: never;
  yieldTargets?: never;
}

export type AssetStrategyBlock =
  | TokenAssetStrategyBlock
  | CanonicalAssetStrategyBlock;

export interface ConditionalStrategyBlock extends BaseStrategyBlock {
  blockType: "conditional";
  children: [AcceptedStrategyBlocks, AcceptedStrategyBlocks];
  // The comparison data is used to determine which child block to execute
  // {evalDataLeft} {evalDataComparitor} {evalDataRight}
  // 'is evalDataLeft computed value greater/less than dataB's computed value?'
  // if true, executes if block, else executes else block (see children tuple above)
  comparisonData: {
    evalDataLeft:
      | CondLeftArgsNoRuntime
      | {
          id: "";
          args: undefined;
        };
    evalDataRight:
      | CondRightArgsNoRuntime
      | {
          id: "";
          args: undefined;
        };
    evalComparison: {
      value: ConditionalComparison;
    };
  };
}

export interface FilterStrategyBlock extends BaseStrategyBlock {
  blockType: "filter";
  data: {
    filters?: {
      functionArgs: FilterFunctionArgsNoRuntime;
      comparator: FilterComparisonOperators;
      value: number;
    }[];
    sort:
      | SortFunctionArgsNoRuntime
      | {
          id: "";
          args: undefined;
        };
    select: SelectFunctionArgsNoRuntime;
  };
  children: (AssetStrategyBlock | PlaceholderStrategyBlock)[];
}

// reexport type WeightType
export type { WeightType };

export interface WeightAllocationGroupBlock extends BaseStrategyBlock {
  blockType: "weight";
  weightType: WeightType;
  // Weightings are out of 100. All weightings in the array must add up to 100. e.g. [30, 70] weightings of $100 capital would mean child[0] gets 30% of the $100, child[1] gets 70% of the $100
  weightings: string[]; // actually strings though
  // NOTE: the weightings tuple is ordering dependent and corresponds to the children index.
  // so weightings[0] corresponds to children[0]. That may change in future to keep more tightly coupled, but we need to control weightings at the parent level, not at the child level.
  children: AcceptedStrategyBlocks[];
}

// This is a wildcard block (we are extracting from the fitler block)
export interface DynamicStrategyBlock extends BaseStrategyBlock {
  blockType: "dynamic";
  children: AcceptedStrategyBlocks[];
  data: {
    wildcardFilter?: WildcardFilterOptions;
    select: SelectFunctionArgsNoRuntime;
  };
}

export interface DynamicStrategyBlockEvaluated extends DynamicStrategyBlock {
  children: AcceptedStrategyBlocks[];
}

export interface RemoteStrategyBlock extends BaseStrategyBlock {
  blockType: "remote";
  data: {
    url: string;
    timeout?: number;
  };
}

export interface RemoteStrategyBlockEvaluated extends RemoteStrategyBlock {
  children: AcceptedStrategyBlocks[];
}

export interface ActionButtonStrategyBlock extends BaseStrategyBlock {
  blockType: "action-button";
}

export type AcceptedStrategyBlocks = BlockType["data"];
export type AcceptedStrategyBlocksEvaluated = BlockTypeEvaluated["data"];

// Type Guards
export function isPlaceholderStrategyBlock(
  block: AcceptedStrategyBlocks,
): block is PlaceholderStrategyBlock {
  return block.blockType === "placeholder";
}

export function isTopLevelStrategyBlock(
  block: AcceptedStrategyBlocks,
): block is TopLevelStrategyBlock {
  return block.blockType === "topLevel";
}

export function isGroupStrategyBlock(
  block: AcceptedStrategyBlocks,
): block is GroupStrategyBlock {
  return block.blockType === "group";
}

export function isAssetStrategyBlock(
  block: AcceptedStrategyBlocks,
): block is AssetStrategyBlock {
  return block.blockType === "asset";
}

export function isCanonicalAssetStrategyBlock(
  block: AcceptedStrategyBlocks,
): block is CanonicalAssetStrategyBlock {
  return block.blockType === "asset" && "assetCanonicalId" in block;
}

export function isTokenAssetStrategyBlock(
  block: AcceptedStrategyBlocks,
): block is TokenAssetStrategyBlock {
  return block.blockType === "asset" && "assetId" in block;
}

/** Stable aggregation key shared by token and canonical asset blocks. */
export function getStrategyAssetKey(block: AssetStrategyBlock): string {
  return isCanonicalAssetStrategyBlock(block)
    ? block.assetCanonicalId
    : block.assetId;
}

export function isConditionalStrategyBlock(
  block: AcceptedStrategyBlocks,
): block is ConditionalStrategyBlock {
  return block.blockType === "conditional";
}

export function isFilterStrategyBlock(
  block: AcceptedStrategyBlocks,
): block is FilterStrategyBlock {
  return block.blockType === "filter";
}

export function isWeightAllocationGroupBlock(
  block: AcceptedStrategyBlocks,
): block is WeightAllocationGroupBlock {
  return block.blockType === "weight";
}

export function isActionButtonStrategyBlock(
  block: AcceptedStrategyBlocks,
): block is ActionButtonStrategyBlock {
  return block.blockType === "action-button";
}

export function isRemoteStrategyBlock(
  block: AcceptedStrategyBlocks,
): block is RemoteStrategyBlock {
  return block.blockType === "remote";
}

export function isDynamicStrategyBlock(
  block: AcceptedStrategyBlocks,
): block is DynamicStrategyBlock {
  return block.blockType === "dynamic";
}

type JSONValue = {
  [key: string]: JSONValue;
};

type JSONCompatible<T> = unknown extends T
  ? never
  : {
      [P in keyof T]: T[P] extends JSONValue
        ? T[P]
        : T[P] extends NotAssignableToJson
          ? never
          : JSONCompatible<T[P]>;
    };

type NotAssignableToJson = bigint | symbol | Function;

// --- Yield-Related Types ---

/**
 * Explicit yield allocation target for an asset.
 * Allows precise control over yield distribution across multiple sources.
 *
 * @example
 * ```typescript
 * // Split 50% to Aave, 30% to Compound
 * yieldTargets: [
 *   { yieldSourceId: "aave:v3:ausdc:8453", percent: 50 },
 *   { yieldSourceId: "compound:v3:cusdc:8453", percent: 30 }
 * ]
 * ```
 */
export interface YieldTarget {
  /** Unique identifier for the yield source (e.g., "aave:v3:ausdc:8453") */
  yieldSourceId: string;
  /** Percentage allocation to this yield source (0-100). Total across all targets must be ≤100. */
  percent: number;
}

/**
 * Details about a specific yield position target.
 * Used in strategy evaluation results.
 */
export interface DesiredYieldPositionDetail {
  yieldSourceId: string; // IYieldSource.id
  resolvedYieldSource?: any; // The actual IYieldSource instance (typed as any to avoid circular deps)
  usdValueTargeted: number; // USD value from strategy nodes targeting this specific yield source
}

/**
 * Aggregated asset allocation including yield targets.
 * Output from strategy evaluation.
 */
export interface AggregatedAssetAllocation {
  /** Concrete token ID for legacy documents; canonical UUID for v2 documents. */
  baseAssetId: string;
  totalUsdAllocation: number; // Total USD value for this base asset from all strategy nodes
  totalUsdTargetedForYield: number; // Portion of totalUsdAllocation intended for any yield
  desiredYieldPositions: DesiredYieldPositionDetail[]; // Specific yield sources and amounts for this base asset
}

/**
 * Represents a current yield holding in the portfolio.
 */
export interface CurrentYieldHolding {
  yieldBearingAssetId: EthOrSolanaAssetIdFormat; // e.g., aUSDC's EthOrSolanaAssetIdFormat
  sharesAmount: bigint; // Raw amount of shares held (smallest unit)
  resolvedYieldSource?: any; // The IYieldSource instance managing this (typed as any to avoid circular deps)
  equivalentBaseAssetAmount: bigint; // Current value in terms of the base asset (smallest unit)
  equivalentUsdValue: number; // Current USD value of this holding
}

/**
 * Current portfolio state including yield positions.
 */
export interface CurrentPortfolioState {
  liquidBaseAssetHoldings: Record<
    EthOrSolanaAssetIdFormat,
    {
      amount: bigint; // Smallest unit
      usdValue: number;
    }
  >;
  yieldHoldings: CurrentYieldHolding[];
  totalPortfolioUsdValue: number;
  timestamp: number; // Timestamp of when this state was captured
}

export interface StrategyMetadata {
  // name: string;
  // description: string;
  // tags: string[];
  tradingSettings:
    | {
        type: "threshold";
        triggerPercentage: number;
      }
    | {
        type: "calendar";
        frequency: string;
      };
}

export interface StrategyBase extends StrategyMetadata {
  entry: TopLevelStrategyBlock;
  version: string;
}

export interface StrategyBaseWithEntryDuckTyped extends StrategyMetadata {
  // Remove all recursive types and generics so we can use zod here to duck type when we send to/from server
  entry: {
    blockType: "topLevel";
    name: string;
    children: {
      blockType: "weight";
      weightType: WeightType;
      weightings: number[];
      children: any[];
    };
    blockIdForInstance: string;
  };
}

// So Strategy should always be serializable (e.g. JSONCompatible in the case of these specific types)
// Same as StrategyBase but with JSONCompatible so make sure we don't accidentally start adding non-serializable
export interface Strategy extends JSONCompatible<StrategyBase> {}

// solver types
export interface InputDataForSolver {
  assets: Array<EthOrSolanaAssetIdFormat>;
  initial_quantities_usd: string[];
  target_distribution_usd: string[];
  swap_costs: Record<AssetPairIdFormat, string>;
  usd_values: Record<EthOrSolanaAssetIdFormat, string>;
}

export interface SolverSolvedDataReturnResultSwap {
  // ONLY VALUES THAT MATTER
  token_in: EthOrSolanaAssetIdFormat;
  token_out: EthOrSolanaAssetIdFormat;
  swap_amount_token: string;
  swap_amount_usd: string;
  // Optional deterministic ID (token_in + token_out pair)
  id?: string;
  //  ALL OPTIONAL
  // deprecated
  estimated_token_out_amount?: string;
  // deprecated
  estimated_token_out_amount_usd?: string;
  // deprecated
  actual_token_out_amount?: string;
  // deprecated
  actual_token_out_amount_usd?: string;
  // deprecated
  price_impact?: string;
}

export interface PortfolioTokenBalanceData {
  assetId: EthOrSolanaAssetIdFormat;
  totalBalance?: Decimal | string | number;
  balanceInUsd?: Decimal | null;
  tokenPriceInUsd?: string;
  balanceInHumanReadableAmount?: string;
  decimals?: number;
  symbol?: string;
  token?: {
    decimals?: number;
  };
  [key: string]: unknown;
}

export interface PortfolioBalancesSnapshot {
  tokenBalancesWithUsdPrices: PortfolioTokenBalanceData[];
  tokenBalancesWithUsdPricesMap: Record<
    EthOrSolanaAssetIdFormat,
    PortfolioTokenBalanceData
  >;
  totalPortfolioValueInUsd: number;
}
